// Centralised rendering defaults.

import type { SliceMaterialKind, Vec3Obj } from '../store/schema';
import { MAGVIZ_BRAND, rgbaBrand } from '../app/themeCss';

export interface AxisExtent {
  min: number;
  max: number;
}

export interface CameraDefaults {
  position: Vec3Obj;
  target: Vec3Obj;
  fovDeg: number;
  near: number;
  far: number;
  minDistance: number;
  maxDistance: number;
  /** Orthographic zoom clamps (OrbitControls uses these instead of
   *  min/maxDistance under an OrthographicCamera). Roughly the zoom-equivalents
   *  of the 0.1/100 distance clamps at the ~23 WU default framing. */
  minZoom: number;
  maxZoom: number;
  dampingFactor: number;
}

export const CAMERA_DEFAULTS: CameraDefaults = {
  position: { x: 15, y: 10, z: 15 },
  target:   { x: 0,  y: 0,  z: 0  },
  fovDeg: 60,
  // near < minDistance so geometry stays visible when zoomed right up to the
  // target (zoom-to-cursor lets the camera reach minDistance from any point).
  near: 0.02,
  far: 1000,
  // minDistance is the camera→target clamp. zoom-to-cursor slides the target
  // toward the point under the cursor as you scroll, so a large minDistance
  // makes zoom stop an arbitrary distance from whatever you're pointing at
  // (the target floats off in empty space). Keep it small so you can close in.
  minDistance: 0.1,
  maxDistance: 100,
  minZoom: 0.2,
  maxZoom: 300,
  // Lower than OrbitControls' 0.05 default for a more cinematic, gliding stop.
  dampingFactor: 0.035,
};

export interface SceneDefaults {
  cameraPhys: Vec3Obj;
  selectionPos: Vec3Obj;
  selectionSize: number;
  axisExtents: { x: AxisExtent; y: AxisExtent; z: AxisExtent };
  gridSpacing: number;
  minorSpacing: number;
  majorSpacing: number;
  showFps: boolean;
}

export const SCENE_DEFAULTS: SceneDefaults = {
  cameraPhys: { x: 0, y: 5, z: 12 },
  selectionPos: { x: 0, y: 2, z: 0 },
  selectionSize: 0.5,
  axisExtents: { x: { min: -10, max: 10 }, y: { min: -5, max: 5 }, z: { min: -5, max: 5 } },
  gridSpacing: 2,
  minorSpacing: 1,
  majorSpacing: 5,
  showFps: true,
};

export interface DatasetDefaults {
  key: string;
  component: string;
  url: string;
}

export const DATASET_DEFAULTS: DatasetDefaults = {
  key: 'dipole_field',
  component: 'magnitude',
  url: 'data/dipole_field.h5',
};

// Mirrors the keys pypicToMagviz emits; widened from 4 → many so consumers
// no longer have to cast through Record<string, string>.
export interface ThemeUI {
  bodyBg: string;
  textColor: string;
  tweakTextColor: string;
  panelBg: string;
  panelBorder: string;
  panelHeaderBg: string;
  panelHeaderBorder: string;
  perfBg: string;
  helpBg: string;
  helpBorder: string;
  inputBg: string;
  buttonBg: string;
  buttonHoverBg: string;
  buttonText: string;
  paneBorder: string;
  sliderTrack: string;
  scrollbarTrack: string;
  scrollbarThumb: string;
  scrollbarThumbBorder: string;
  scrollbarThumbHover: string;
  accent: string;
  thumbBorder: string;
  sliderFill: string;
  sliderGrip: string;
  sliderTick: string;
  sliderRadius: string;
  sliderHeight: string;
  sliderGripSize: string;
}

// Font tokens come from pypic's [font] table. Sizes are unitless in TOML;
// we treat them as px (the values 9–18 align with our hand-tuned chrome).
export interface ThemeFont {
  family: string[];
  sizeTitle: number;
  sizeLabel: number;
  sizeTick: number;
  sizeOverlay: number;
}

/** Selection visual tokens. Live here (theme.scene.selection) rather than
 * sceneMeta because they're appearance-only — color, opacity, dash sizing.
 * Currently consumed only by src/scene/picking/setup.ts; structured so the
 * fields can be promoted to per-theme TOML later without code churn.
 *
 * `sphereColor: null` → fall back to `theme.ui.accent` so the selection sphere
 * picks up whatever accent the theme defines without duplicating the color.
 *
 * `projectionOpacity: null` → derive from `scene.grid.majorOpacity` plus a
 * small boost so the dashed helper lines stay readable over busy slices.
 */
export interface ThemeSelection {
  sphereColor: string | null;
  glow: boolean;
  glowOpacity: number;
  projectionOpacity: number | null;
  projectionDashSize: number;
  projectionGapSize: number;
  // Marker tunables (optional; code defaults in picking/marker.ts + setup.ts).
  outlineScale?: number; // outline ring size ÷ sphere radius
  hoverScale?: number; // extra ring scale at full hover
  hoverBrighten?: number; // core lerp-to-white at full hover (0..1)
  pulseScale?: number; // extra ring scale at the click-pulse peak
  crossLength?: number; // crosshair arm half-length, world units
  // Drag handles (touch-friendly knobs on stems above/beside the marker).
  handle?: boolean; // build the handles (default true; gated also by `glow`)
  handleOffsetScale?: number; // knob distance from core ÷ sphere radius
  handleScale?: number; // knob sprite size ÷ sphere radius
  handleIdleOpacity?: number; // ↕ knob/stem opacity at rest (0..1)
  hHandleIdleOpacity?: number; // ↔ knob/stem opacity at rest (0..1; subtler)
}

export interface ThemeScene {
  background: string;
  grid: { color: string; minorOpacity: number; majorOpacity: number };
  gridLabelColor: string;
  vectorLabelColor: string | null;
  selection: ThemeSelection;
}

export interface ThemeAxes {
  x: string | null;
  y: string | null;
  z: string | null;
  arrows: boolean;
}

export interface ThemeDefaults {
  name: string | null;
  ui: ThemeUI;
  font: ThemeFont;
  scene: ThemeScene;
  axes: ThemeAxes;
  cycle: string[];
  colormaps: { sequential: string | null; diverging: string | null };
}

// Shape matches pypicToMagviz output so downstream consumers see a consistent
// schema before any theme has loaded.
export const THEME_DEFAULTS: ThemeDefaults = {
  name: null,
  ui: {
    bodyBg: '#000000',
    textColor: '#ffffff',
    tweakTextColor: '#cfe0ff',
    panelBg: 'rgba(15, 20, 35, 0.38)',
    panelBorder:           rgbaBrand(MAGVIZ_BRAND.blueSoft, 0.22),
    panelHeaderBg:         'rgba(30, 40, 70, 0.3)',
    panelHeaderBorder:     rgbaBrand(MAGVIZ_BRAND.blueSoft, 0.30),
    perfBg:                'rgba(15, 20, 35, 0.5)',
    helpBg:                'rgba(15, 20, 35, 0.6)',
    helpBorder:            rgbaBrand(MAGVIZ_BRAND.blueSoft, 0.30),
    inputBg:               'rgba(24, 30, 50, 0.9)',
    buttonBg:              'rgba(58, 110, 255, 0.25)',
    buttonHoverBg:         'rgba(74, 126, 255, 0.35)',
    buttonText:            '#dfe9ff',
    paneBorder:              rgbaBrand(MAGVIZ_BRAND.blueSoft, 0.18),
    sliderTrack:           rgbaBrand(MAGVIZ_BRAND.blueGlow, 0.32),
    scrollbarTrack:        'rgba(30, 40, 70, 0.10)',
    scrollbarThumb:        rgbaBrand(MAGVIZ_BRAND.blueGlow, 0.32),
    scrollbarThumbBorder:  rgbaBrand(MAGVIZ_BRAND.blueGlow, 0.22),
    scrollbarThumbHover:   rgbaBrand(MAGVIZ_BRAND.blueGlow, 0.42),
    accent:                MAGVIZ_BRAND.accent,
    thumbBorder:           '#ffffff',
    sliderFill:            MAGVIZ_BRAND.accent,
    sliderGrip:            MAGVIZ_BRAND.accent,
    sliderTick:            rgbaBrand(MAGVIZ_BRAND.blueGlow, 0.25),
    sliderRadius:          '7px',
    sliderHeight:          '3px',
    sliderGripSize:        '21px',
  },
  font: {
    family: ['Segoe UI', 'Arial', 'sans-serif'],
    sizeTitle: 16,
    sizeLabel: 13,
    sizeTick: 12,
    sizeOverlay: 13,
  },
  scene: {
    background: '#000000',
    grid: { color: '#ffffff', minorOpacity: 0.15, majorOpacity: 0.35 },
    gridLabelColor: '#808080',
    vectorLabelColor: null,
    selection: {
      sphereColor: null,
      glow: true,
      glowOpacity: 0.7,
      projectionOpacity: null,
      projectionDashSize: 0.28,
      projectionGapSize: 0.10,
    },
  },
  axes: { x: null, y: null, z: null, arrows: true },
  cycle: [],
  colormaps: { sequential: null, diverging: null },
};

export interface SliceDefaults {
  resolution: number;
  pixelated: boolean;
  material: SliceMaterialKind;
  depthWrite: boolean;
  depthTest: boolean;
  doubleSide: boolean;
  alphaTest: number;
  metalness: number;
  roughness: number;
  renderOrder: number;
  castShadow: boolean;
  receiveShadow: boolean;
}

export const SLICE_DEFAULTS: SliceDefaults = {
  // Resolution is computed from data in UI builder; this is a fallback only.
  resolution: 256,
  pixelated: true,
  material: 'standard',
  depthWrite: true,
  depthTest: true,
  doubleSide: true,
  alphaTest: 0.0,
  metalness: 0.0,
  roughness: 1.0,
  renderOrder: 10,
  castShadow: true,
  receiveShadow: true,
};

export interface FieldlineDefaults {
  tubes: boolean;
  opacity: number;
  radius: number;
  material: string;
  depthWrite: boolean;
  depthTest: boolean;
  renderOrder: number;
  castShadow: boolean;
  receiveShadow: boolean;
}

export const FIELDLINE_DEFAULTS: FieldlineDefaults = {
  tubes: true,
  opacity: 1.0,
  radius: 0.03,
  material: 'standard', // 'standard' | 'lambert' | 'phong'
  depthWrite: true,
  depthTest: true,
  renderOrder: 0,
  castShadow: true,
  receiveShadow: true,
};

export interface ContourDefaults {
  opacity: number;
  metalness: number;
  roughness: number;
  envIntensity: number;
  doubleSide: boolean;
  depthWrite: boolean;
  renderOrder: number;
}

export const CONTOUR_DEFAULTS: ContourDefaults = {
  opacity: 1.0,
  metalness: 0.10,
  roughness: 0.75,
  envIntensity: 0.0,
  doubleSide: true,
  depthWrite: true,
  renderOrder: 10,
};

export interface LabelDefaults {
  fontFamily: string;
  weight?: string;
  fontWeight?: string;
  opacity: number;
  padding?: number;
  widthWorld?: number;
  heightScale?: number;
  alphaTest: number;
  depthTest: boolean;
  depthWrite: boolean;
  polygonOffset: boolean;
  polygonOffsetFactor: number;
}

// Text/label defaults for axes and grids.
export const AXIS_LABEL_DEFAULTS: LabelDefaults = {
  fontFamily: 'Helvetica Neue, Arial, sans-serif',
  weight: 'bold',
  opacity: 0.9,
  padding: 0.3,
  alphaTest: 0.01,
  depthTest: true,
  depthWrite: false,
  polygonOffset: true,
  polygonOffsetFactor: -1,
};

export const GRID_LABEL_DEFAULTS: LabelDefaults = {
  fontFamily: 'Helvetica Neue, Arial, sans-serif',
  fontWeight: '500',
  opacity: 0.9,
  widthWorld: 1.2,
  heightScale: 0.6,
  alphaTest: 0.01,
  depthTest: true,
  depthWrite: false,
  polygonOffset: true,
  polygonOffsetFactor: -1,
};
